import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Loader2 } from "lucide-react";
import NumaMascot from "@/components/NumaMascot";
import { auth, legal as legalApi, normalizeError } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { takeSignOutReason } from "@/lib/session";
import LegalDialog from "@/components/LegalDialog";

const ROLES = [
  { id: "student", label: "Studente" },
  { id: "researcher", label: "Ricercatore" },
  { id: "faculty", label: "Docente" },
];

export default function Login() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("researcher");
  const [accepted, setAccepted] = useState(false);
  // Off by default: the session that ends by itself is the safe one, and
  // staying signed in on a shared machine should be a deliberate choice.
  const [remember, setRemember] = useState(false);
  const [legalDoc, setLegalDoc] = useState(null);
  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(false);
  // Shown in the form itself. A toast disappears while the person is still
  // reading the field it is about.
  const [problem, setProblem] = useState(null);
  // Quando il link per rimettere la password è partito: la pagina lo dice e
  // resta lì, invece di riportare l'autore a un modulo che sembra non aver
  // fatto niente.
  const [linkSentTo, setLinkSentTo] = useState("");
  const { login, register } = useAuth();
  const navigate = useNavigate();

  // Set when the watchdog closed the session: said here, on arrival, because
  // being back at the sign-in form without an explanation reads as a fault.
  const [closedReason] = useState(() => takeSignOutReason());

  // Fetched so that acceptance is recorded against exactly the text shown.
  useEffect(() => {
    legalApi
      .owner()
      .then((d) => setVersions({ privacy: d.privacy_version, terms: d.terms_version }))
      .catch(() => setVersions(null));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setProblem(null);

    // Checked here so the rule is stated before the round trip, not after.
    if (mode !== "forgot" && mode === "register") {
      if (password.length < 10) {
        setProblem({ code: "password", message: "La password deve avere almeno 10 caratteri." });
        return;
      }
      if (/^[a-zA-Z]+$/.test(password) || /^[0-9]+$/.test(password)) {
        setProblem({
          code: "password",
          message: "Aggiungi almeno un numero o un simbolo alla password.",
        });
        return;
      }
    }

    setLoading(true);
    try {
      if (mode === "forgot") {
        const esito = await auth.forgotPassword(email);
        setLinkSentTo(esito.email || email);
        return;
      }
      if (mode === "login") {
        await login({ email, password, remember });
      } else {
        await register({
          email,
          password,
          name: name || email.split("@")[0],
          role,
          accept_terms: true,
          privacy_version: versions?.privacy,
          terms_version: versions?.terms,
        });
      }
      navigate("/", { replace: true });
    } catch (err) {
      const e = normalizeError(err);
      setProblem({
        code: e.code,
        message: e.message || "Autenticazione non riuscita",
        action: e.action,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-paper" data-testid="login-page">
      {/*
        L'unico titolo di primo livello della pagina pubblica.

        Quello grande sta nella colonna di sinistra, che sotto i 1024px non
        viene mostrata: e siccome Google indicizza guardando la versione
        mobile, senza questo la pagina non ne avrebbe nessuno. Stesso testo,
        letto dai lettori di schermo e dai crawler.
      */}
      <h1 className="sr-only">NUMA — ambiente di scrittura e revisione accademica</h1>
      {/* Identity column: the mascot lives here, at rest, where it sets the tone
          without competing with the form. */}
      <aside className="hidden w-[46%] max-w-[560px] flex-col justify-between border-r border-line bg-surface-2 px-12 py-14 lg:flex">
        <div className="flex items-center gap-2.5">
          <NumaMascot size={34} className="numa-mark" variant={0} />
          <span className="font-serif-display text-lg font-semibold tracking-tight text-ink">NUMA</span>
        </div>

        <div className="max-w-sm">
          <h2 className="font-serif-display text-[32px] font-semibold leading-[1.2] text-ink">
            Un ambiente di scrittura accademica che non scrive al posto tuo.
          </h2>
          <p className="mt-5 text-[14.5px] leading-relaxed text-ink-muted">
            Struttura, revisione critica, gestione delle fonti ed esportazione conforme —
            per tesi, volumi, articoli, rassegne e progetti di ricerca.
          </p>
          <dl className="mt-10 space-y-4 border-t border-line pt-8">
            <div>
              <dt className="font-medium text-[13.5px] text-ink">Il tuo account, il tuo modello</dt>
              <dd className="numa-meta mt-0.5">
                Colleghi il tuo account OpenRouter con un clic, oppure una API key di
                Anthropic, OpenAI, Google e altri. Nessun abbonamento a NUMA.
              </dd>
            </div>
            <div>
              <dt className="font-medium text-[13.5px] text-ink">I tuoi testi restano tuoi</dt>
              <dd className="numa-meta mt-0.5">
                Nessuna analisi di terze parti, nessuna registrazione di sessione.
              </dd>
            </div>
          </dl>
        </div>

        <p className="text-[12px] text-ink-subtle">© 2026 NUMA</p>
      </aside>

      {/* Form column */}
      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-[380px]">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <NumaMascot size={30} className="numa-mark" variant={0} />
            <span className="font-serif-display text-lg font-semibold text-ink">NUMA</span>
          </div>

          <h2 className="font-serif-display text-2xl font-semibold text-ink">
            {mode === "login" ? "Accedi"
              : mode === "register" ? "Crea un account"
              : "Password dimenticata"}
          </h2>
          <p className="numa-meta mt-1">
            {mode === "login"
              ? "Riprendi da dove avevi lasciato."
              : mode === "register"
                ? "Bastano un'email e una password."
                : "Scrivi l'indirizzo con cui ti sei registrato: ti mandiamo un link per sceglierne una nuova."}
          </p>

          {closedReason === "inattivita" && !problem && (
            <div
              data-testid="auth-idle-notice"
              role="status"
              className="mt-6 rounded-md border border-line bg-surface-2 px-3.5 py-3"
            >
              <p className="text-[13px] leading-relaxed text-ink-muted">
                Sessione chiusa dopo 15 minuti di inattività. Accedi di nuovo per riprendere:
                il lavoro è salvato. Spunta <strong className="font-medium text-ink">Ricordami</strong> se
                sei sul tuo dispositivo.
              </p>
            </div>
          )}

          {linkSentTo && (
            <div
              data-testid="reset-link-sent"
              role="status"
              className="mt-6 rounded-md border border-ok/40 bg-ok/5 px-3.5 py-3"
            >
              <p className="text-[13px] font-medium text-ink">
                Link inviato a {linkSentTo}
              </p>
              <p className="numa-meta mt-1 leading-relaxed">
                Apri il messaggio e scegli la password nuova. Il link vale due ore e si usa una
                volta sola. Se non lo vedi, guarda nella posta indesiderata.
              </p>
            </div>
          )}

          {problem && (
            <div
              data-testid="auth-error"
              role="alert"
              className="mt-6 rounded-md border border-danger/40 bg-danger/5 px-3.5 py-3"
            >
              <p className="flex items-start gap-2 text-[13px] font-medium text-ink">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-danger" />
                {problem.message}
              </p>
              {problem.action && <p className="numa-meta mt-1 pl-[22px]">{problem.action}</p>}
              {problem.code === "email_taken" && (
                <div className="mt-2.5 flex flex-wrap gap-2 pl-[22px]">
                  <button
                    type="button"
                    data-testid="switch-to-login"
                    onClick={() => { setMode("login"); setProblem(null); setPassword(""); }}
                    className="rounded-md bg-brand px-3 py-1.5 text-[12.5px] font-medium text-on-solid hover:bg-brand-hover"
                  >
                    Accedi con questa email
                  </button>
                  <button
                    type="button"
                    onClick={() => { setEmail(""); setProblem(null); }}
                    className="rounded-md border border-line-strong px-3 py-1.5 text-[12.5px] text-ink-muted hover:bg-surface-2"
                  >
                    Usa un'altra email
                  </button>
                </div>
              )}
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    data-testid="register-name-input"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nome e cognome"
                    className="h-10"
                  />
                </div>
                <fieldset className="space-y-1.5">
                  <legend className="mb-1.5 text-sm font-medium text-ink">Ruolo</legend>
                  <div className="grid grid-cols-3 gap-1.5">
                    {ROLES.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setRole(r.id)}
                        aria-pressed={role === r.id}
                        className={`rounded-md border px-2 py-2 text-[13px] transition-colors ${
                          role === r.id
                            ? "border-brand bg-brand-soft font-medium text-brand"
                            : "border-line-strong text-ink-muted hover:bg-surface-2"
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                  <p className="numa-meta">Adatta il linguaggio della revisione. Modificabile dopo.</p>
                </fieldset>
              </>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                data-testid="login-email-input"
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-10"
              />
            </div>

            {mode !== "forgot" && (
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                data-testid="login-password-input"
                id="password"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10"
              />
              {mode === "register" && (
                <p className="numa-meta">Almeno 10 caratteri, con una cifra o un simbolo.</p>
              )}
              {mode === "login" && (
                <button
                  type="button"
                  data-testid="forgot-password"
                  onClick={() => { setMode("forgot"); setProblem(null); setPassword(""); }}
                  className="text-[12.5px] text-brand hover:underline"
                >
                  Hai dimenticato la password?
                </button>
              )}
            </div>
            )}

            {mode === "login" && (
              <label className="flex cursor-pointer items-start gap-2.5">
                <Checkbox
                  id="remember"
                  data-testid="remember-me"
                  checked={remember}
                  onCheckedChange={(v) => setRemember(v === true)}
                  className="mt-0.5"
                />
                <span className="text-[12.5px] leading-relaxed text-ink-muted">
                  <span className="font-medium text-ink">Ricordami su questo dispositivo</span>
                </span>
              </label>
            )}

            {mode === "register" && (
              <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-line bg-surface-2 px-3 py-2.5">
                <Checkbox
                  id="accept-terms"
                  data-testid="accept-terms"
                  checked={accepted}
                  onCheckedChange={(v) => setAccepted(v === true)}
                  className="mt-0.5"
                />
                <span className="text-[12.5px] leading-relaxed text-ink-muted">
                  Accetto le{" "}
                  <button
                    type="button"
                    onClick={() => setLegalDoc("terms")}
                    className="font-medium text-brand hover:underline"
                  >
                    condizioni d&apos;uso
                  </button>{" "}
                  e dichiaro di aver preso visione dell&apos;
                  <button
                    type="button"
                    onClick={() => setLegalDoc("privacy")}
                    className="font-medium text-brand hover:underline"
                  >
                    informativa privacy
                  </button>
                  .
                </span>
              </label>
            )}

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading || (mode === "register" && !accepted)}
              className="h-10 w-full"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {mode === "login" ? "Accedi" : mode === "register" ? "Crea account" : "Mandami il link"}
            </Button>
          </form>

          <p className="mt-6 text-[13px] text-ink-muted">
            {mode === "forgot" ? "Hai ricordato la password? " : mode === "login" ? "Non hai un account? " : "Hai già un account? "}
            <button
              type="button"
              data-testid="toggle-mode-btn"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setProblem(null);
                setLinkSentTo("");
              }}
              className="font-medium text-brand hover:underline"
            >
              {mode === "login" ? "Registrati" : "Accedi"}
            </button>
          </p>

          <p className="mt-8 border-t border-line pt-5 text-[12.5px] leading-relaxed text-ink-subtle">
            Il modello si collega al primo utilizzo di una funzione assistita, non ora.
          </p>

          <p className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-ink-subtle">
            <button type="button" onClick={() => setLegalDoc("privacy")} className="hover:text-ink hover:underline">
              Privacy
            </button>
            <button type="button" onClick={() => setLegalDoc("terms")} className="hover:text-ink hover:underline">
              Condizioni d&apos;uso
            </button>
            <a href="mailto:info@numa-studio.eu" className="hover:text-ink hover:underline">
              Assistenza
            </a>
          </p>
        </div>

        <LegalDialog
          open={Boolean(legalDoc)}
          onOpenChange={(o) => !o && setLegalDoc(null)}
          document={legalDoc || "privacy"}
        />
      </main>
    </div>
  );
}
